import { getLorebookAttachBlockedReason, useLorebook, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { SubCapabilityKey, useApplicationCapabilitiesAllowed } from "@next-platform/client-common-core/infra/application/capabilities";
import {
  filterLorebooksBySearch,
  getDisplayLorebookData,
  LorebookSelectionSort,
  sortLorebooksByCreatedAt,
} from "@next-platform/client-common-core/lorebook/selection";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  DropDownMenu,
  DropDownMenuItem,
  IconVariants,
  Input,
  ListEmpty,
  LorebookCard,
  Modal,
  ModalSize,
  Spinner,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { Lorebook, LorebookVisibility } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS, ROUTES } from "@/constants";
import { useFeatureFlag } from "@/hooks";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import LorebookDiscoverTab from "./LorebookDiscoverTab";

enum LorebookModalTab {
  MyLorebooks = "myLorebooks",
  Favorites = "favorites",
  Discover = "discover",
}

interface IModalLorebookProps {
  isOpen?: boolean;
  onClose: () => void;
  onLorebookAttached?: (lorebook: Lorebook) => void;
  selectedLorebook?: Lorebook | null;
  dataTestId?: string;
}

const SEARCH_DEBOUNCE_MS = 400;

export default function ModalLorebook({ isOpen, onClose, onLorebookAttached, selectedLorebook, dataTestId = "" }: IModalLorebookProps) {
  const { t } = useTranslation();
  const [stateData, setStateData] = useState<Lorebook[] | null>(null);
  const [stateSearchQuery, setStateSearchQuery] = useState("");
  const [stateSort, setStateSort] = useState<LorebookSelectionSort>(LorebookSelectionSort.Latest);
  const [debouncedSearchQuery] = useDebounce(stateSearchQuery, SEARCH_DEBOUNCE_MS);
  const navigate = useNavigate();
  const { onlyUserId: userId, isAuthenticated } = useUserInfo();
  const { blurNSFW, likedLorebooks } = useUserSettings();
  const { getLorebooksData, isLoading: isLorebookLoading, lorebooksData } = useLorebook();
  const [stateSelectedLorebook, setStateSelectedLorebook] = useState<Lorebook | null>(null);
  const [stateFilter, setStateFilter] = useState<LorebookModalTab>(LorebookModalTab.MyLorebooks);

  // Blur NSFW avatars for the same audience as the rest of the app: signed-out users always,
  // and signed-in users who have the "blur NSFW" setting on.
  const shouldBlurNsfwAvatar = !isAuthenticated || blurNSFW;

  const { hasFeatureFlag: hasLorebookDiscoverPublic } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_DISCOVER_PUBLIC);
  const [isLorebookPublicDiscoveryAllowed] = useApplicationCapabilitiesAllowed([SubCapabilityKey.LorebookPublicDiscovery]);
  const isDiscoverAvailable = hasLorebookDiscoverPublic && isLorebookPublicDiscoveryAllowed;
  const isDiscoverFilter = isDiscoverAvailable && stateFilter === LorebookModalTab.Discover;
  const isFavoritesFilter = isDiscoverAvailable && stateFilter === LorebookModalTab.Favorites;
  const isSearchIndexFilter = isDiscoverFilter || isFavoritesFilter;
  const filterFavoriteIds = isFavoritesFilter ? likedLorebooks : null;

  // Switching tabs clears the current selection: a lorebook selected in one tab may not be
  // present in the other tab's list, which would otherwise leave Attach enabled for a card
  // the user can no longer see.
  const handleFilterChange = useCallback((filter: LorebookModalTab) => {
    setStateFilter(filter);
    setStateSelectedLorebook(null);
  }, []);

  const filterItems = useMemo<DropDownMenuItem[]>(
    () => [
      {
        // TODO(MuhammadZubair210): [NX-11357] replace with an i18n key once the My Lorebooks label is added to the translation platform
        label: "My Lorebooks",
        value: LorebookModalTab.MyLorebooks,
        onPress: () => handleFilterChange(LorebookModalTab.MyLorebooks),
      },
      {
        label: t("search:botContext.favorites"),
        value: LorebookModalTab.Favorites,
        onPress: () => handleFilterChange(LorebookModalTab.Favorites),
      },
      {
        label: t("search:botContext.discover"),
        value: LorebookModalTab.Discover,
        onPress: () => handleFilterChange(LorebookModalTab.Discover),
      },
    ],
    [t, handleFilterChange],
  );
  const filterLabel = useMemo(() => {
    const matchedFilterItem = filterItems.find((filterItem) => filterItem.value === stateFilter);
    return matchedFilterItem?.label ?? "My Lorebooks";
  }, [filterItems, stateFilter]);

  // Only Active lorebooks are attachable. The shared helper mirrors the API's attach guard — notably,
  // a public lorebook whose edit is under review keeps its approved version attachable —
  // and names the reason, which picks the inline error below. Each case also disables the CTA.
  const blockedReason = useMemo(() => getLorebookAttachBlockedReason(stateSelectedLorebook), [stateSelectedLorebook]);
  const isSelectedUnderReview = blockedReason === "underReview";
  const isSelectedActionRequired = blockedReason === "actionRequired";
  const isSelectedNeedsReview = blockedReason === "needsReview";
  const isSelectedBlocked = blockedReason !== null;

  // If the currently attached lorebook stays selected there is nothing to replace, so the CTA is disabled.
  const isSelectionUnchanged = Boolean(selectedLorebook?.id) && stateSelectedLorebook?.id === selectedLorebook?.id;

  const sortedData = useMemo(() => {
    if (!stateData) return null;
    return sortLorebooksByCreatedAt(stateData, stateSort);
  }, [stateData, stateSort]);

  const filteredData = useMemo(() => {
    if (!sortedData) return null;
    return filterLorebooksBySearch(sortedData, debouncedSearchQuery);
  }, [sortedData, debouncedSearchQuery]);

  // Always show the selected lorebook in the list while it is selected, even if it doesn't match the search
  const displayData = useMemo(
    () => getDisplayLorebookData(sortedData, filteredData, stateSelectedLorebook?.id ?? null),
    [sortedData, filteredData, stateSelectedLorebook?.id],
  );

  const resultsCountValues = useMemo(() => ({ count: displayData?.length ?? 0 }), [displayData?.length]);
  const sortItems = useMemo<DropDownMenuItem[]>(
    () => [
      {
        label: t("search:sortBy.latest"),
        value: LorebookSelectionSort.Latest,
        onPress: () => setStateSort(LorebookSelectionSort.Latest),
      },
      {
        label: t("search:sortBy.oldest"),
        value: LorebookSelectionSort.Oldest,
        onPress: () => setStateSort(LorebookSelectionSort.Oldest),
      },
    ],
    [t],
  );
  const sortLabel = useMemo(() => {
    const matchedSortItem = sortItems.find((sortItem) => sortItem.value === stateSort);
    return matchedSortItem?.label ?? t("search:sortBy.latest");
  }, [sortItems, stateSort, t]);
  const confirmLabel = selectedLorebook?.id ? t("chatbot:form.section.lorebook.button.replace") : t("chatbot:form.section.lorebook.button.attach");
  useEffect(() => {
    if (isOpen) {
      setStateSelectedLorebook(selectedLorebook ?? null);
      setStateSearchQuery("");
      setStateSort(LorebookSelectionSort.Latest);
      setStateFilter(LorebookModalTab.MyLorebooks);
    } else {
      setStateSelectedLorebook(null);
      setStateSearchQuery("");
      setStateSort(LorebookSelectionSort.Latest);
      setStateFilter(LorebookModalTab.MyLorebooks);
    }
  }, [isOpen, selectedLorebook]);

  async function fetchData() {
    void getLorebooksData();
  }

  useEffect(() => {
    if (lorebooksData) {
      setStateData(lorebooksData as Lorebook[]);
    }
  }, [lorebooksData]);

  useEffect(() => {
    // we only fetch data if we have a logged in user AND when the modal is open
    if (userId && isOpen) void fetchData();
    return () => setStateData(null);
  }, [userId, isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!stateSelectedLorebook || isSelectedBlocked || isSelectionUnchanged) return;
    onLorebookAttached?.(stateSelectedLorebook);
    onClose();
  };

  const handleClickLorebook = useCallback(
    (lorebook: Lorebook) => () => {
      setStateSelectedLorebook(lorebook);
    },
    [setStateSelectedLorebook],
  );

  // Discover and Favorites results come from the public (search) index, so they are already
  // approved and never surface a pending-review state for the current user.
  const handleSelectDiscoverLorebook = useCallback(
    (lorebook: Lorebook) => {
      setStateSelectedLorebook(lorebook);
    },
    [setStateSelectedLorebook],
  );

  // Discover and Favorites data come from the public search index, not the owned-lorebooks REST
  // fetch, so their actions must not be gated on isLorebookLoading (that request is unrelated here).
  const isActionsLoading = isSearchIndexFilter ? false : isLorebookLoading;

  const actions = [
    {
      label: t("common:cancel"),
      variant: ButtonVariant.TEXT,
      type: "button",
      onClick: handleClose,
      disabled: isActionsLoading,
    },
    {
      label: confirmLabel,
      variant: ButtonVariant.CONTAINED,
      type: "submit",
      disabled: isActionsLoading || !stateSelectedLorebook || isSelectedBlocked || isSelectionUnchanged,
      onClick: handleSubmit,
    },
  ] as React.ReactNode;

  const renderFilterDropdown = () => {
    if (!isDiscoverAvailable) return null;
    return (
      <DropDownMenu listItems={filterItems} placement="bottom-end">
        <div>
          <Button
            type="button"
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.MEDIUM}
            iconEnd={IconVariants.CHEVRON_DOWN}
            aria-label={filterLabel}
          >
            {filterLabel}
          </Button>
        </div>
      </DropDownMenu>
    );
  };

  const renderSortDropdown = () => (
    <DropDownMenu listItems={sortItems} placement="bottom-end">
      <div>
        <Button type="button" variant={ButtonVariant.OUTLINED} size={ButtonSize.MEDIUM} iconEnd={IconVariants.CHEVRON_DOWN} aria-label={sortLabel}>
          {sortLabel}
        </Button>
      </div>
    </DropDownMenu>
  );

  // Keep the footer (so Cancel/Replace stay reachable) whenever the user is on Discover/Favorites,
  // owns lorebooks to act on, or already has a selection — e.g. a public lorebook attached earlier
  // while owning zero of their own, reopened on the My Lorebooks tab.
  const isActionsVisible = isSearchIndexFilter || Boolean(stateData?.length) || Boolean(stateSelectedLorebook);

  return (
    <Modal
      title={t("lorebook:modal.attachLorebook.title")}
      open={isOpen}
      onOpenChange={onClose}
      actions={isActionsVisible ? actions : null}
      size={ModalSize.MEDIUM}
      description={
        <div className="flex flex-col gap-4" data-testid={dataTestId}>
          {isSelectedUnderReview ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
              <Trans i18nKey="lorebook:modal.attachLorebook.pendingReview" />
            </Text>
          ) : null}
          {isSelectedActionRequired ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
              {/* TODO(MuhammadZubair210): [NX-11358] replace with an i18n key once localization copy is registered */}
              This Lorebook needs changes and can&apos;t be attached. Please choose another.
            </Text>
          ) : null}
          {isSelectedNeedsReview ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
              {/* TODO(MuhammadZubair210): [NX-11358] replace with an i18n key once localization copy is registered */}
              This Lorebook needs review and can&apos;t be attached. Please choose another.
            </Text>
          ) : null}
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
            <Trans i18nKey="lorebook:modal.attachLorebook.description" />
          </Text>
          {!isSearchIndexFilter && !isLorebookLoading && !!stateData?.length && (
            <>
              <Input
                placeholder={t("myCreation:search.lorebooks")}
                iconStart={IconVariants.SEARCH}
                value={stateSearchQuery}
                onChange={(value) => setStateSearchQuery(value ?? "")}
              />
              <div className="flex justify-between items-center">
                {!!displayData?.length && (
                  <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
                    <Trans i18nKey="common:results.found" values={resultsCountValues} />
                  </Text>
                )}
                <div className="flex items-center gap-2">
                  {renderFilterDropdown()}
                  {renderSortDropdown()}
                </div>
              </div>
            </>
          )}
          {!isSearchIndexFilter && !isLorebookLoading && !stateData?.length && isDiscoverAvailable ? (
            <div className="flex justify-end items-center">{renderFilterDropdown()}</div>
          ) : null}
        </div>
      }
    >
      {/* Minimum height keeps the modal tall enough for the filter dropdown to open without being
          clipped by the modal's overflow container when the list is empty or short. */}
      <div className="flex flex-col gap-4 min-h-[400px]">
        <div className="flex flex-col flex-1">
          {isSearchIndexFilter ? (
            <LorebookDiscoverTab
              key={isFavoritesFilter ? "favorites" : "discover"}
              selectedLorebook={stateSelectedLorebook}
              onSelect={handleSelectDiscoverLorebook}
              renderFilterControl={renderFilterDropdown}
              renderSortControl={renderSortDropdown}
              sortOrder={stateSort}
              favoriteIds={filterFavoriteIds}
            />
          ) : (
            <>
              {isLorebookLoading ? (
                <div className="flex flex-1 justify-center items-center">
                  <Spinner className="mx-auto" />
                </div>
              ) : null}
              {!isLorebookLoading && (!stateData?.length || !displayData?.length) && (
                <div className="flex flex-1 justify-center items-center">
                  <ListEmpty
                    isLoading={false}
                    title={t("common:noResultsFound")}
                    description={
                      stateData?.length
                        ? t("lorebook:modal.attachLorebook.emptyState.noResults.description")
                        : t("myCreation:emptyState.lorebook.description")
                    }
                    buttonText={stateData?.length ? undefined : t("lorebook:title.create")}
                    onClick={stateData?.length ? undefined : () => navigate(ROUTES.lorebookCreate)}
                  />
                </div>
              )}
              {!isLorebookLoading && !!displayData?.length && (
                <div className="flex flex-col gap-md">
                  {displayData.map((dataInfo: Lorebook) => (
                    <LorebookCard
                      key={dataInfo?.id}
                      id={dataInfo?.id}
                      avatarUrl={getAvatarUrl(dataInfo?.avatar_url ?? "", { returnWithOriginalRatio: true }) as string}
                      name={dataInfo?.name}
                      isPrivate={dataInfo?.visibility === LorebookVisibility.PRIVATE}
                      isBlurred={Boolean(shouldBlurNsfwAvatar && dataInfo?.avatar_is_nsfw)}
                      description={dataInfo?.description}
                      numEntries={dataInfo?.num_entries ?? 0}
                      creatorUsername={dataInfo?.creator_username}
                      onClick={handleClickLorebook(dataInfo)}
                      isSelected={stateSelectedLorebook?.id === dataInfo?.id}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
